import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { API_URLS } from 'src/app/config/api-urls';
import { QueryResponse } from 'src/app/shared/models/query-response';
import { AccountDetails } from '../store/accounts.state';
import { NewSubscription, Subscriptions } from './models/subscriptions.model';
import { AccountNotes } from './models/account-notes.model';
import { AccountSearchReturn } from './models/account-search.model';
import { AccountTeachers, NewAccountTeacher, TeacherEmailValidationStatus } from './models/account-teachers.model';
import { AccountDetailsForm, AccountSchoolExternalIdentifier } from './models/account-details.model';
import { AccountStudents, StudentGroups } from './models/account-students.model';
import { UsageReport } from './models/account-usage.model';

@Injectable({
  providedIn: 'root'
})
export class AccountApiService {
  private http = inject(HttpClient);

  private accountsSearchUrl = API_URLS.ACCOUNTS_SEARCH;
  private loadAccountUrl = API_URLS.LOAD_ACCOUNT;
  private loadSubscriptionUrl = API_URLS.LOAD_SUBSCRIPTIONS;
  private addSubscriptionsUrl = API_URLS.ADD_SUBSCRIPTIONS;
  private editSubscriptionUrl = API_URLS.EDIT_SUBSCRIPTION;
  private addSiteLicenceUrl = API_URLS.ADD_SITELICENCE;
  private addFreeTrialUrl = API_URLS.ADD_FREE_TRIAL;
  private addLiteracyOfferUrl = API_URLS.ADD_LITERACY_OFFER;
  private loadAccountNotesUrl = API_URLS.LOAD_ACCOUNT_NOTES;
  private submitAccountNoteUrl = API_URLS.SUBMIT_ACCOUNT_NOTE;
  private updateAccountNoteUrl = API_URLS.UPDATE_ACCOUNT_NOTE;
  private exportAccountUrl = API_URLS.EXPORT_ACCOUNT;
  private updateAccountDetailsUrl = API_URLS.UPDATE_ACCOUNT_DETAILS;
  private resendWelcomeEmailUrl = API_URLS.RESEND_WELCOME_EMAIL;
  private resendHubSpotIntegrationMessageUrl = API_URLS.RESEND_HUBSPOT_INTEGRATION_MESSAGE;
  private changeAccountLanguageUrl = API_URLS.CHANGE_ACCOUNT_LANGUAGE;
  private changeAccountStatusUrl = API_URLS.CHANGE_ACCOUNT_STATUS;
  private deleteAccountUrl = API_URLS.DELETE_ACCOUNT;
  private resetAccountPasswordUrl = API_URLS.RESET_ACCOUNT_PASSWORD;
  private updateExternalSchoolIdentifierUrl = API_URLS.UPDATE_EXTERNAL_SCHOOL_IDENTIFIER;

  private loadAccountTeachersUrl = API_URLS.LOAD_ACCOUNT_TEACHERS;
  private addAccountTeacherUrl = API_URLS.ADD_ACCOUNT_TEACHER;
  private deleteAccountTeacherUrl = API_URLS.DELETE_ACCOUNT_TEACHER;
  private resendTeacherWelcomeEmailUrl = API_URLS.RESEND_TEACHER_WELCOME_EMAIL;
  private checkEmailAvailabilityUrl = API_URLS.CHECK_EMAIL_AVAILABILITY;

  private loadAccountStudentsUrl = API_URLS.LOAD_ACCOUNT_STUDENTS;
  private accountUsageUrl = API_URLS.LOAD_ACCOUNT_USAGE;

  searchAccounts(Search: string, Property: string, pageSize: number, showAnonymized: boolean): Observable<QueryResponse<AccountSearchReturn>> {
    const params = new HttpParams()
      .set('Search', Search)
      .set('Property', Property)
      .set('pageSize', pageSize)
      .set('pageNumber', 1)
      .set('showAnonymised', showAnonymized);

    return this.http.get<QueryResponse<AccountSearchReturn>>(this.accountsSearchUrl, {
      params
    });
  }

  loadAccount(accountId: number) {
    const params = new HttpParams().set('accountId', accountId);

    return this.http.get<QueryResponse<AccountDetails>>(this.loadAccountUrl, {
      params
    });
  }

  loadAccountSubscriptions(accountId: number) {
    const params = new HttpParams().set('accountId', accountId);

    return this.http.get<QueryResponse<Subscriptions>>(this.loadSubscriptionUrl, {
      params
    });
  }

  addAccountSubscription(accountId: number, subscription: NewSubscription) {
    return this.http.post<QueryResponse>(this.addSubscriptionsUrl, {
      accountId: accountId,
      applicationType: subscription.applicationType,
      subscriptionType: subscription.subscriptionType,
      startDate: subscription.subscriptionStartDate,
      endDate: subscription.subscriptionEndDate,
      maxLicenses: subscription.maxLicences,
      subscriptionStatus: subscription.subscriptionStatus,
      isSiteLicense: subscription.isSiteLicense,
      sendEmails: subscription.sendEmails
    });
  }

  editAccountSubscription(subscription: NewSubscription) {
    return this.http.post<QueryResponse>(this.editSubscriptionUrl, {
      subscriptionId: subscription.subscriptionId,
      subscriptionType: subscription.subscriptionType,
      subscriptionStatus: subscription.subscriptionStatus,
      startDate: subscription.subscriptionStartDate,
      endDate: subscription.subscriptionEndDate,
      maxLicenses: subscription.maxLicences,
      isSiteLicense: subscription.isSiteLicense
    });
  }

  addSiteLicence(accountId: number, subscription: NewSubscription) {
    return this.http.post<QueryResponse>(this.addSiteLicenceUrl, {
      accountId: accountId,
      licenseCount: subscription.maxLicences,
      startDate: subscription.subscriptionStartDate,
      endDate: subscription.subscriptionEndDate,
      subscriptionType: subscription.subscriptionType,
      isSiteLicense: subscription.isSiteLicense,
      sendEmails: subscription.sendEmails
    });
  }

  addFreeTrial(accountId: number, subscription: NewSubscription) {
    return this.http.post<QueryResponse>(this.addFreeTrialUrl, {
      accountId: accountId,
      licenseCount: subscription.maxLicences,
      startDate: subscription.subscriptionStartDate,
      endDate: subscription.subscriptionEndDate,
      isSiteLicense: subscription.isSiteLicense,
      sendEmails: subscription.sendEmails
    });
  }

  addLiteracyOffer(accountId: number, subscription: NewSubscription) {
    return this.http.post<QueryResponse>(this.addLiteracyOfferUrl, {
      accountId: accountId,
      licenseCount: subscription.maxLicences,
      startDate: subscription.subscriptionStartDate,
      endDate: subscription.subscriptionEndDate,
      sendEmails: subscription.sendEmails,
      subscriptionType: subscription.subscriptionType
    });
  }

  loadAccountNotes(accountId: number) {
    const params = new HttpParams().set('accountId', accountId);

    return this.http.get<QueryResponse<AccountNotes>>(this.loadAccountNotesUrl, {
      params
    });
  }

  submitAccountNote(accountId: number, note: string) {
    return this.http.post<QueryResponse>(this.submitAccountNoteUrl, {
      accountId: accountId,
      text: note
    });
  }

  updateAccountNote(noteId: number, note: string) {
    return this.http.post<QueryResponse>(this.updateAccountNoteUrl, {
      noteId: noteId,
      text: note
    });
  }

  exportAccount(accountId: number) {
    const params = new HttpParams().set('accountId', accountId);

    return this.http.get<string>(this.exportAccountUrl, {
      params,
      responseType: 'text' as 'json'
    });
  }

  updateAccountDetails(accountId: number, accountDetails: Partial<AccountDetailsForm>) {
    return this.http.post<QueryResponse>(this.updateAccountDetailsUrl, { AccountId: accountId, ...accountDetails });
  }

  resendWelcomeEmail(accountId: number, isUDEmail: boolean): Observable<QueryResponse> {
    return this.http.post<QueryResponse>(this.resendWelcomeEmailUrl, {
      accountId: accountId,
      sendUdEmail: isUDEmail
    });
  }

  resendHubSpotIntegrationMessage(accountId: number): Observable<QueryResponse> {
    return this.http.post<QueryResponse>(this.resendHubSpotIntegrationMessageUrl, accountId,
    );
  }

  changeAccountLanguage(accountId: number, language: string) {
    return this.http.post<QueryResponse>(this.changeAccountLanguageUrl, {
      accountId: accountId,
      language: language
    });
  }

  changeAccountStatus(accountId: number, disableAccount: boolean) {
    return this.http.post<QueryResponse>(this.changeAccountStatusUrl, {
      accountId: accountId,
      disableAccount: disableAccount
    });
  }

  deleteAccount(accountId: number) {
    return this.http.post<QueryResponse>(this.deleteAccountUrl, {
      accountId: accountId
    });
  }

  loadAccountTeachers(accountId: number, searchTerm: string | null) {
    let params = new HttpParams().set('accountId', accountId);

    if (searchTerm) {
      params = params.set('search', searchTerm);
    }

    return this.http.get<QueryResponse<AccountTeachers>>(this.loadAccountTeachersUrl, {
      params
    });
  }

  editOrAddTeacher(accountId: number, teacher: NewAccountTeacher): Observable<QueryResponse> {
    return this.http.post<QueryResponse>(this.addAccountTeacherUrl, { AccountId: accountId, ...teacher });
  }

  deleteTeacher(userId: string): Observable<QueryResponse> {
    return this.http.post<QueryResponse>(this.deleteAccountTeacherUrl, { userGuid: userId });
  }

  resendTeacherWelcomeEmail(accountId: number, userId: string, isUdTrainee: boolean): Observable<QueryResponse> {
    return this.http.post<QueryResponse>(this.resendTeacherWelcomeEmailUrl, {
      accountId: accountId,
      recipientUserId: userId,
      sendUdEmail: isUdTrainee
    });
  }

  checkEmailAvailability(email: string): Observable<QueryResponse<TeacherEmailValidationStatus>> {
    return this.http.post<QueryResponse<TeacherEmailValidationStatus>>(this.checkEmailAvailabilityUrl, {
      email: email
    });
  }

  loadAccountStudents(
    accountId: number,
    pageSize: number,
    searchTerm?: string,
    group?: string
  ): Observable<QueryResponse<AccountStudents>> {
    let params = new HttpParams().set('accountId', accountId).set('pageSize', pageSize).set('pageNumber', 1);
    if (searchTerm) {
      params = params.set('search', searchTerm);
    }
    if (group) {
      params = params.set('groups', group);
    }

    return this.http.get<QueryResponse<AccountStudents>>(this.loadAccountStudentsUrl, {
      params
    });
  }

  loadStudentGroups(accountId: number): Observable<QueryResponse<StudentGroups[]>> {
    const params = new HttpParams().set('accountId', accountId);

    return this.http.get<QueryResponse<StudentGroups[]>>(API_URLS.LOAD_STUDENT_GROUPS, {
      params
    });
  }

  syncStudentLicenses(studentId: number): Observable<QueryResponse> {
    return this.http.post<QueryResponse>(API_URLS.SYNC_STUDENT_LICENSES, 
      studentId
    );
  }

  syncAllAccountsStudentLicenses(accountId: number): Observable<QueryResponse> {
    return this.http.post<QueryResponse>(API_URLS.SYNC_ALL_ACCOUNTS_STUDENT_LICENSES, 
      accountId
    );
  }

  resetAccountPassword(
    accountId: number,
    userId: string,
    isUdTrainee: boolean,
    isAdmin: boolean
  ): Observable<QueryResponse> {
    return this.http.post<QueryResponse>(this.resetAccountPasswordUrl, {
      accountId: accountId,
      recipientUserId: userId,
      sendUdEmail: isUdTrainee,
      toAccountAdmin: isAdmin
    });
  }

  updateExternalSchoolIdentifier(
    accountId: number,
    externalSchoolIdentifierDetails: AccountSchoolExternalIdentifier
  ): Observable<QueryResponse> {
    return this.http.post<QueryResponse>(this.updateExternalSchoolIdentifierUrl, {
      accountId: accountId,
      externalSchoolIdentifier: externalSchoolIdentifierDetails.externalSchoolIdentifier,
      externalSchoolParentIdentifier: externalSchoolIdentifierDetails.externalSchoolParentIdentifier,
      externalSchoolIdDataSourceId: Number(externalSchoolIdentifierDetails.externalSchoolIdDataSourceId)
    });
  }

  loadAccountUsage(accountId: number, startDate?: string, endDate?: string): Observable<QueryResponse<UsageReport>> {
    let params = new HttpParams().set('accountId', accountId);
    if (startDate) {
      params = params.set('startDate', startDate);
    }
    if (endDate) {
      params = params.set('endDate', endDate);
    }

    return this.http.get<QueryResponse<UsageReport>>(this.accountUsageUrl, { params });
  }
}
